/**
 * The Data Studio landing page.
 *
 * `/tenant/:tenant` has never had one: it redirects to `/models`, so the models
 * list IS the landing page and has been a file listing. It says which models
 * exist and says nothing about whether any of them is working.
 *
 * This keeps the list as the spine and answers the three questions someone
 * arriving actually has: is production scoring with this, how is that going,
 * and does the result reach anybody. Two of the three come from bongo and are
 * allowed to be absent, so every column here degrades to nothing rather than
 * taking the page with it.
 *
 * `TenantPage` is untouched and still mounted for tenants without the flag.
 * This is a separate component picked at the mount point rather than a rewrite
 * in place, so flag-off renders exactly what shipped before, including the
 * parts this page drops.
 */
import { inject, observer } from 'mobx-react';
import React from 'react';
import { Alert, Button, Form, Nav } from 'react-bootstrap';
import { NavLink, RouteComponentProps, withRouter } from 'react-router-dom';

import ModelItem, { ModelType } from '../../models/ModelItem';
import { checkIsArchitect } from '../../permissions';
import Store from '../../store';
import { TrainingSource } from '../../models/trainingSource';
import Error from '../components/error/Error';
import { stashHandoff } from '../components/model/askBus';
import AskDataStudio from '../components/models/AskDataStudio';
import {
  matchesSearch,
  TYPE_ICON,
  typeLabel,
  typesPresent,
} from '../components/models/modelFamilies';
import {
  fetchModelsLiveStatus,
  ModelsLiveStatus,
} from '../components/models/modelsHomeApi';
import {
  nextSort,
  Sort,
  SortKey,
  sortIcon,
  sortModels,
  cleanNote,
} from '../components/models/modelSorting';
import { onModelsRefresh } from '../components/models/modelsRefreshBus';
import ModelRowActions from '../components/models/ModelRowActions';
import PerformanceBars from '../components/models/PerformanceBars';
import PushChip from '../components/models/PushChip';
import Spinner from '../components/Spinner';
import { Icon } from '../components/ui/icon';
import { canSeeDataStudio, isV2User } from '../utils/rbac';
import './modelsHome.css';

/** The tiers the live reading is summed over, in ramp order. */
const SEGMENTS = ['very good', 'good', 'medium', 'low'];

/**
 * The audience and conversion a draft is built on.
 *
 * Two lines rather than one: they are two separate definitions someone picked
 * in the dataset builder, and joined by a separator they read as one string.
 * Models built from a CSV or a Redshift table have neither, and say what they
 * do have instead, so an empty cell always means nobody has configured one
 * rather than "this build has no audience".
 */
/**
 * Primary or Secondary, where that means anything.
 *
 * Only Customer Fit and Account Engagement support a standard model plus
 * secondaries, which is the split `ModelStatus` already makes. The engagement
 * families have no such pair, so labelling one of them "Primary" invents a
 * distinction the product does not have.
 *
 * Said as a detail under the status rather than in place of it. A primary fit
 * model IS live, and a column that reads "Live" on one row and "Primary fit" on
 * the next asks the reader to work out that the two are not alternatives.
 */
/** The brand icon for a type; `light` on the dark, selected filter chip. */
function TypeIcon({ type, light }: { type: ModelType; light?: boolean }) {
  const icon = TYPE_ICON[type];
  if (!icon) return null;
  return (
    <img
      className="mh-type-icon"
      src={light ? icon.white : icon.navy}
      alt=""
      aria-hidden
    />
  );
}

function fitRank(model: ModelItem): string | null {
  if (model.type !== 'customer_fit' && model.type !== 'mqa') return null;
  return model.is_primary ? 'Primary' : 'Secondary';
}

/** One dataset's two lines: what it selects, and what it counts as a conversion. */
function renderDataset({ audience, conversionType, from }: TrainingSource) {
  if (from) return <span className="mh-aud">{from}</span>;
  return (
    <>
      <span className="mh-aud">{audience ?? 'Any audience'}</span>
      <span className="mh-conv">{conversionType ?? 'No conversion set'}</span>
    </>
  );
}

function renderTrainedOn(trainedOn: TrainingSource | undefined) {
  // Undefined while the batch route is still in flight, and the row should not
  // claim "not set" before anything has been read.
  if (!trainedOn) return <span className="mh-muted">-</span>;
  const { audience, conversionType, from, validation } = trainedOn;
  if (!audience && !conversionType && !from) {
    return <span className="mh-muted">Not set yet</span>;
  }
  // Only when the two datasets actually differ, which `trainingSourceOf` decides. A
  // model that validates on what it trained on is the ordinary case and says so by
  // showing one thing; naming both halves on every row would make the exception
  // invisible by making it the format.
  if (!validation) {
    return <span className="mh-trained">{renderDataset(trainedOn)}</span>;
  }
  return (
    <span className="mh-trained">
      <span className="mh-split">
        <span className="mh-split-label">Trains on</span>
        {renderDataset(trainedOn)}
      </span>
      <span className="mh-split">
        <span className="mh-split-label">Validates on</span>
        {renderDataset(validation)}
      </span>
    </span>
  );
}

interface ModelsHomeProps extends RouteComponentProps<{ tenant: string }> {
  store?: Store;
}

interface ModelsHomeState {
  loading: boolean;
  status: ModelsLiveStatus;
  /** One search per section: the two lists are browsed for different reasons. */
  liveQuery: string;
  draftQuery: string;
  liveSort: Sort;
  draftSort: Sort;
  type: ModelType | 'all';
  showArchived: boolean;
}

const ModelsHome = inject('store')(
  observer(
    class ModelsHome extends React.Component<ModelsHomeProps, ModelsHomeState> {
      constructor(props: ModelsHomeProps) {
        super(props);
        this.state = {
          loading: true,
          status: {},
          liveQuery: '',
          draftQuery: '',
          liveSort: { key: 'deployed', dir: 'desc' },
          draftSort: { key: 'updated', dir: 'desc' },
          type: 'all',
          showArchived: false,
        };
      }

      /** Unsubscribes from the assistant's model writes. */
      offModelsRefresh: () => void;

      async componentDidMount() {
        const { store } = this.props;
        await store.getTenantModels();

        // The assistant creates, renames and archives through the same routes the
        // page uses, so a model changes and the browser never hears about it. Same
        // mechanism the tree pane uses for `tree_refresh`.
        this.offModelsRefresh = onModelsRefresh(() => {
          void this.reload();
        });
        // Not awaited together with the models: the list is the page and the
        // live status decorates it, so the table paints as soon as it can and
        // the extra columns fill in. A slow bongo should cost the columns, not
        // the first render.
        this.setState({ loading: false });
        const status = await fetchModelsLiveStatus(store.tenant, SEGMENTS);
        this.setState({ status });
      }

      componentWillUnmount() {
        this.offModelsRefresh?.();
      }

      /**
       * Re-read the models and their live status.
       *
       * Both, because a duplicate is a new row with no production record and a rename
       * changes a row the status is keyed to. In order rather than together: the list
       * is the page and the status decorates it, so the table should not wait on
       * bongo to show the name that just changed.
       */
      async reload() {
        const { store } = this.props;
        await store.getTenantModels();
        const status = await fetchModelsLiveStatus(store.tenant, SEGMENTS);
        this.setState({ status });
      }

      /**
       * The rows of one section, filtered by the type pills (which are global)
       * and by that section's own search box, then sorted by its own column.
       */
      rowsFor(live: boolean, query: string, sort: Sort): ModelItem[] {
        const { tenantModels } = this.props.store;
        const { type, showArchived } = this.state;
        const rows = (tenantModels ?? []).filter(
          (model) =>
            !!model.live === live &&
            (live || showArchived || !model.is_archived) &&
            (type === 'all' || model.type === type) &&
            matchesSearch(model, query)
        );
        return sortModels(rows, sort);
      }

      /** A sortable column header. */
      sortableHeader(
        label: string,
        key: SortKey,
        sort: Sort,
        onSort: (sort: Sort) => void
      ) {
        return (
          <th scope="col">
            <button
              type="button"
              className="mh-sort"
              aria-pressed={sort.key === key}
              onClick={() => onSort(nextSort(sort, key))}
            >
              {label}
              <Icon icon={sortIcon(sort, key)} />
            </button>
          </th>
        );
      }

      /** A section's own search box, in its header rather than above the card. */
      sectionSearch(
        id: string,
        value: string,
        onChange: (next: string) => void
      ) {
        return (
          <div className="mh-search">
            <label className="mh-sr" htmlFor={id}>
              Search these models
            </label>
            <input
              id={id}
              type="search"
              autoComplete="off"
              placeholder="Search name, ID or type..."
              value={value}
              onChange={(event) => onChange(event.target.value)}
            />
          </div>
        );
      }

      renderFilters(models: ModelItem[]) {
        const { type } = this.state;
        const present = typesPresent(models.map((model) => model.type));
        return (
          <div className="mh-filters">
            <div
              className="mh-types"
              role="group"
              aria-label="Filter by model type"
            >
              <button
                type="button"
                className="mh-type"
                aria-pressed={type === 'all'}
                onClick={() => this.setState({ type: 'all' })}
              >
                All <span className="mh-count">{models.length}</span>
              </button>
              {present.map((modelType) => (
                <button
                  key={modelType}
                  type="button"
                  className="mh-type"
                  aria-pressed={type === modelType}
                  onClick={() => this.setState({ type: modelType })}
                >
                  <TypeIcon type={modelType} light={type === modelType} />
                  {typeLabel(modelType)}{' '}
                  <span className="mh-count">
                    {models.filter((model) => model.type === modelType).length}
                  </span>
                </button>
              ))}
            </div>
          </div>
        );
      }

      renderName(model: ModelItem) {
        const { tenant } = this.props.match.params;
        return (
          <div className="mh-name">
            <div className="mh-name-top">
              <a href={`/tenant/${tenant}/models/${model.modelId}`}>
                {model.name}
              </a>
              {!!cleanNote(model.notes) && (
                <span className="mh-note">
                  {/* A memo, not the sparkle this had before: the sparkle is
                      this product's mark for something a model generated, and
                      these are notes a person typed. */}
                  <button type="button" aria-label="Model notes">
                    <Icon icon="far fa-memo" />
                  </button>
                  <span className="mh-note-pop" role="tooltip">
                    <span className="mh-note-h">Model notes</span>
                    <span className="mh-note-body">
                      {cleanNote(model.notes)}
                    </span>
                  </span>
                </span>
              )}
            </div>
            <span className="mh-type-chip">
              <TypeIcon type={model.type} />
              {typeLabel(model.type)}
            </span>
          </div>
        );
      }

      renderLive(models: ModelItem[]) {
        const { status, liveSort, liveQuery } = this.state;
        const onSort = (liveSort2: Sort) =>
          this.setState({ liveSort: liveSort2 });
        return (
          <section className="mh-card" aria-label="Live models">
            <div className="mh-sec">
              <h3>Live</h3>
              <span className="mh-n">{models.length}</span>
              {this.sectionSearch('live-search', liveQuery, (liveQuery2) =>
                this.setState({ liveQuery: liveQuery2 })
              )}
            </div>
            {models.length ? (
              <div className="mh-scroll">
                <table className="mh-table">
                  <thead>
                    <tr>
                      {this.sortableHeader('ID', 'modelId', liveSort, onSort)}
                      {this.sortableHeader('Model', 'name', liveSort, onSort)}
                      <th scope="col">Live performance</th>
                      <th scope="col">Integrations</th>
                      {this.sortableHeader(
                        'Deployed',
                        'deployed',
                        liveSort,
                        onSort
                      )}
                      <th scope="col">Status</th>
                      {/* No label: the column is three dots, and a heading
                          over it would be wider than the thing it names. */}
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((model) => (
                      <tr key={model.modelId}>
                        <td className="mh-id">{model.modelId}</td>
                        <td>{this.renderName(model)}</td>
                        <td>
                          <PerformanceBars
                            live={status[model.modelId]?.performance ?? null}
                          />
                        </td>
                        <td>
                          <PushChip status={status[model.modelId]?.push} />
                        </td>
                        <td>
                          <span className="mh-when">
                            <span className="mh-d">
                              {model.lastDeployedAt?.slice(0, 10) ?? '-'}
                            </span>
                            <span className="mh-w">
                              {model.lastDeployedBy ?? ''}
                            </span>
                          </span>
                        </td>
                        <td>
                          <span className="mh-status">
                            <span className="mh-chip mh-live">Live</span>
                            {fitRank(model) && (
                              <span className="mh-rank">{fitRank(model)}</span>
                            )}
                          </span>
                        </td>
                        <ModelRowActions model={model} />
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mh-empty">No live model matches.</div>
            )}
          </section>
        );
      }

      renderDrafts(models: ModelItem[]) {
        const { showArchived, draftSort, draftQuery, status } = this.state;
        const onSort = (draftSort2: Sort) =>
          this.setState({ draftSort: draftSort2 });
        return (
          <section className="mh-card" aria-label="Draft models">
            <div className="mh-sec">
              <h3>Draft</h3>
              <span className="mh-n">{models.length}</span>
              <Form.Check
                className="mh-archived"
                id="switch-archived"
                type="switch"
                label="Show archived"
                checked={showArchived}
                onChange={() => this.setState({ showArchived: !showArchived })}
              />
              {this.sectionSearch('draft-search', draftQuery, (draftQuery2) =>
                this.setState({ draftQuery: draftQuery2 })
              )}
            </div>
            {models.length ? (
              <div className="mh-scroll">
                <table className="mh-table">
                  <thead>
                    <tr>
                      {this.sortableHeader('ID', 'modelId', draftSort, onSort)}
                      {this.sortableHeader('Model', 'name', draftSort, onSort)}
                      <th scope="col">Trained on</th>
                      {this.sortableHeader(
                        'Updated',
                        'updated',
                        draftSort,
                        onSort
                      )}
                      <th scope="col">Status</th>
                      <th scope="col">
                        <span className="visually-hidden">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {models.map((model) => {
                      return (
                        <tr key={model.modelId}>
                          <td className="mh-id">{model.modelId}</td>
                          <td>{this.renderName(model)}</td>
                          <td>
                            {renderTrainedOn(status[model.modelId]?.trainedOn)}
                          </td>
                          <td>
                            <span className="mh-when">
                              <span className="mh-d">
                                {model.updatedAt?.slice(0, 10) ?? '-'}
                              </span>
                            </span>
                          </td>
                          <td>
                            <span
                              className={`mh-chip ${
                                model.is_archived ? 'mh-arch' : 'mh-draft'
                              }`}
                            >
                              {model.is_archived ? 'Archived' : 'Draft'}
                            </span>
                          </td>
                          <ModelRowActions model={model} />
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="mh-empty">No draft matches.</div>
            )}
          </section>
        );
      }

      render() {
        const { store } = this.props;
        const { tenant } = this.props.match.params;
        const { rootPage, rbac, isSuperKudu, userPermissions } = store;
        const { loading, liveQuery, draftQuery, liveSort, draftSort } =
          this.state;

        if (loading) return <Spinner />;

        const all = store.tenantModels ?? [];
        const live = this.rowsFor(true, liveQuery, liveSort);
        const drafts = this.rowsFor(false, draftQuery, draftSort);

        // Same rule as the route gate in StudioPage: RBAC v2 gates on the
        // module assignment, v1 keeps the architect rule, SuperKudu bypasses.
        const canCreateModel = isSuperKudu
          ? true
          : isV2User(rbac)
          ? canSeeDataStudio(rbac)
          : checkIsArchitect(store.tenant, userPermissions);

        return (
          <div className="mh">
            <div className="mh-head">
              {/* Demoted rather than removed. The sidebar already says Models
                  and the topbar already says Data Studio, so as a 21px title
                  this was the third time the page named itself. It stays an h2
                  carrying `page-header-models` because the e2e suite locates
                  the page by that id and waits for it to be VISIBLE, so hiding
                  it is a spec change even though deleting the words is not. */}
              <h2 id="page-header-models">Models</h2>
              <span className="mh-total">
                {all.filter((model) => model.live).length} live ·{' '}
                {
                  all.filter((model) => !model.live && !model.is_archived)
                    .length
                }{' '}
                draft
              </span>
              {canCreateModel && (
                <Nav.Link
                  className="mh-create"
                  as={NavLink}
                  to={`/tenant/${tenant}/models/new`}
                >
                  <Button variant="primary" id="create-new-model-button">
                    <i className="fas fa-plus" /> Create new model
                  </Button>
                </Nav.Link>
              )}
            </div>

            {rootPage.createModelError && (
              <Error message={rootPage.createModelError} />
            )}

            <AskDataStudio
              tenant={tenant}
              onTrack={(eventName, properties) =>
                this.props.store.analyticsTrack(eventName, properties)
              }
              onOpenModel={(modelId, handoff) => {
                // A REAL navigation, not history.push. `isModelIdValid` is computed
                // during server-side render, so a model page reached by client-side
                // routing has never had it computed and redirects to 404. Every other
                // link into a model on this page is a plain anchor for the same
                // reason.
                stashHandoff(modelId, handoff);
                window.location.href = `/tenant/${tenant}/models/${modelId}`;
              }}
            />

            {/* The type pills stay global because they answer a question about
                the org, not about one list. The search boxes live inside each
                card, because searching the 22 drafts and searching the 7 live
                models are different errands. */}
            {this.renderFilters(all)}

            {this.renderLive(live)}
            {this.renderDrafts(drafts)}

            {!all.length && (
              <Alert variant="info" className="mt-3">
                No models have been created for this tenant yet.
                {canCreateModel && ' Create a new model to get started.'}
              </Alert>
            )}
          </div>
        );
      }
    }
  )
);

export default withRouter(ModelsHome);
